/**
 * #1016 — the trader's own Polymarket US orders for the game Game Detail shows.
 *
 * US orders are not held in memory the way Kalshi and Polymarket orders are,
 * so this reads them: every visible account's own saved orders through the
 * shared #1006 reader, then the catalogue evidence that ties each one to a game
 * (`lib/polymarketUs/gameOpenOrders`). #1233: the same read carries the
 * trader's holdings, which the held snapshot shows.
 *
 * #1233 — cadence (only for a login that may have a US account): a read on
 * each mount, login and game, and on an explicit
 * refresh (Retry, after a sweep, after a US order is recorded); then one every
 * 10 s while the page is visible (the Tracker's and side rail's US cadence),
 * none while it is hidden, one as soon as it is visible again. Never two at
 * once for the same login and game: a refresh asked for during a read runs
 * once more when that read settles, so its answer is never one taken before
 * the change that asked for it.
 *
 * A re-read keeps the rows on screen while it runs. A failed re-read keeps
 * them too, marked stale (`staleCode`), as the rail and Tracker keep theirs;
 * the game sweep never sends from a stale read.
 *
 * Every read gets fresh client instances: the order ticket's and the Tracker's
 * clients are never borrowed or disturbed, and a read abandoned by a game or
 * login change can only settle into its own discarded clients. A result is kept
 * with the login and game it was read for, and a changed key shows nothing of
 * it.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import { usGameKeyForBoardKey } from '@/lib/polymarketUs/gameMapping';
import type { UsGameOrderSelection } from '@/lib/polymarketUs/gameOpenOrders';
import { useUsAccountPresence } from '@/hooks/useUsAccountPresence';
import { useAuthStore } from '@/stores/authStore';
import { useLowBandwidth } from '@/stores/lowBandwidth';
import type { UsBulkInventory } from '../PositionTracker/usBulkCancelModel';
import { readUsSessionKey, US_TRACKER_POLL_MS } from '../PositionTracker/usTrackerFeed';
import { readUsGameOrders, type UsGameHoldings, type UsGameReadClients } from './usGameOrdersRead';

/** The visible re-read under low bandwidth mode (#1233's 10 s otherwise). */
export const US_LOW_BANDWIDTH_POLL_MS = 30_000;

export type UsGameOrders =
  /** Not an NFL/CFB game, or US is not available to this login. */
  | { phase: 'off' }
  | { phase: 'loading' }
  | { phase: 'error'; code: string }
  | { phase: 'ready'; selection: UsGameOrderSelection; inventory: UsBulkInventory; holdings: UsGameHoldings;
      readAtMs: number;
      /** Set when the latest re-read failed: everything else is the last read
       * that answered, taken at `readAtMs`. Null while reads answer. */
      staleCode: string | null };

const OFF: UsGameOrders = { phase: 'off' };
const LOADING: UsGameOrders = { phase: 'loading' };
const codeOf = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const freshClients = (): UsGameReadClients => ({
  accounts: createUsBrowserAccountClient(),
  portfolio: createUsBrowserPortfolioClient(),
  markets: createUsBrowserMarketClient(),
});

export interface GameUsOpenOrders {
  orders: UsGameOrders;
  /** The login and game `orders` belongs to; null when there is no US game. */
  identity: string | null;
  /** The login alone, for checks between the awaits of a sweep. */
  sessionKey: string;
  /** Read again (Retry, after a sweep). */
  refresh: () => void;
}

/**
 * `externalSignal` re-reads when it changes (a US order recorded from this
 * panel's ticket). `makeClients` is for tests.
 */
export function useGameUsOpenOrders(
  boardKey: string,
  externalSignal = 0,
  makeClients: () => UsGameReadClients = freshClients,
): GameUsOpenOrders {
  const usGameKey = useMemo(() => usGameKeyForBoardKey(boardKey), [boardKey]);
  const sessionKey = useSyncExternalStore(subscribeAuth, readUsSessionKey);
  const authed = useAuthStore((s) => s.status === 'authenticated' && !!s.token && !!s.user?.orgId);
  // A login known to have no US account reads nothing and polls nothing: the
  // shared presence read (#987, one per login) answers that, as for the rail.
  const presence = useUsAccountPresence(!!usGameKey && authed);
  const live = !!usGameKey && authed && presence !== false;
  const identity = live ? `${sessionKey}|${usGameKey}` : null;
  const [stamped, setStamped] = useState<{ identity: string; value: UsGameOrders } | null>(null);
  const stateRef = useRef(stamped);
  // The identity on screen now: a read for any other one is abandoned.
  const identityRef = useRef(identity);
  const clientsRef = useRef(makeClients);
  useLayoutEffect(() => {
    identityRef.current = identity;
    clientsRef.current = makeClients;
  });
  const inFlight = useRef<string | null>(null);
  const again = useRef<string | null>(null);
  // Each new read loop supersedes the one before it, so a loop left running
  // by a game change (A -> B -> A) cannot land beside the newer A loop.
  const runSeq = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const read = useCallback(async () => {
    if (!identity || !usGameKey) return;
    // A refresh held from an earlier render (a sweep that outlived its game)
    // must not claim the read lock from the game on screen now.
    if (identityRef.current !== identity) return;
    if (inFlight.current === identity) {
      again.current = identity;
      return;
    }
    inFlight.current = identity;
    const run = ++runSeq.current;
    const current = () => mounted.current && runSeq.current === run && identityRef.current === identity
      && readUsSessionKey() === sessionKey;
    const apply = (value: UsGameOrders) => {
      if (!current()) return;
      const next = { identity, value };
      stateRef.current = next;
      setStamped(next);
    };
    try {
      do {
        again.current = null;
        try {
          const value = await readUsGameOrders(clientsRef.current(), usGameKey, Date.now, current);
          apply({ phase: 'ready', ...value, staleCode: null });
        } catch (error) {
          const code = codeOf(error);
          const prev = stateRef.current?.identity === identity ? stateRef.current.value : null;
          // No US for this login is not a failure to report on every game.
          if (code === 'US_CLIENT_AUTH_REQUIRED') apply(OFF);
          else if (prev?.phase === 'ready') apply({ ...prev, staleCode: code });
          else apply({ phase: 'error', code });
        }
      } while (again.current === identity && current());
    } finally {
      if (runSeq.current === run) {
        inFlight.current = null;
        again.current = null;
      }
    }
  }, [identity, usGameKey, sessionKey]);

  // A read for each login and game, and for each recorded US order.
  useEffect(() => {
    void read();
  }, [read, externalSignal]);

  // Low bandwidth mode (lane, 2026-10-19): the visible re-read slows to 30 s; a recorded order and a return to the tab
  // still read at once.
  const pollMs = useLowBandwidth() ? US_LOW_BANDWIDTH_POLL_MS : US_TRACKER_POLL_MS;
  useEffect(() => {
    if (!identity) return;
    let lastPollMs = performance.now();
    const poll = () => {
      if (document.hidden) return;
      lastPollMs = performance.now();
      void read();
    };
    const onVisibility = () => {
      if (performance.now() - lastPollMs >= pollMs) poll();
    };
    const intervalId = window.setInterval(poll, pollMs);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [identity, read, pollMs]);

  const refresh = useCallback(() => { void read(); }, [read]);
  const orders = !identity ? OFF : stamped?.identity === identity ? stamped.value : LOADING;
  return { orders, identity, sessionKey, refresh };
}
